import { setActiveLocationId } from '@borradh-workspace/api-client';
import { Outlet, createFileRoute, redirect } from '@tanstack/react-router';

import { Logo } from '@/components/global/logo';
import { ImpersonationBanner } from '@/features/admin-terminal/components/impersonation-banner';
import { BackgroundAnalysisProvider } from '@/features/assets/background-analysis';
import { SignedInIndicator } from '@/features/auth/components/signed-in-indicator';
import { authRedirectSearchValue } from '@/lib/auth-redirect';
import { ensureSession } from '@/lib/session';
import { useHoldBuildRefresh } from '@/lib/use-unsaved-work-guard';

/**
 * Pathless layout for onboarding pages. Requires auth but renders without the
 * sidebar. Mirrors `apps/web/src/app/(onboarding)/layout.tsx` (session + email
 * verification + chrome).
 */
export const Route = createFileRoute('/_onboarding')({
  beforeLoad: async ({ location, preload }) => {
    const session = await ensureSession();
    if (!session.user) {
      throw redirect({
        to: '/sign-in',
        search: { redirect: authRedirectSearchValue(location) },
      });
    }
    // Organization creation is only for accounts that do not already belong
    // to a business. Profile setup shares this layout and stays accessible.
    // Mailbox verification remains optional for sales-led onboarding.
    if (location.pathname === '/onboarding' && session.user.organizationId) {
      throw redirect({ to: '/dashboard' });
    }
    // No branch header in onboarding. These pages sit outside
    // `ActiveLocationScope`, and the org they set up has no location yet, so
    // any branch the api-client is still holding belongs to someone else —
    // typically the previous account in this tab. Sent along, it made the API
    // 404 the wizard's own writes (services, locations, team) while the org
    // itself was created, leaving an empty business behind.
    //
    // Not on a preload: hovering a dashboard link to `/setup-profile` runs this
    // too, and must not strip the branch from the page the user is still on.
    if (!preload) setActiveLocationId(null);
  },
  component: OnboardingLayout,
});

function OnboardingLayout() {
  // Onboarding spans several routes and its progress is not saved (ENG-624),
  // so a full page load between steps could send a new signup back to the
  // start. Nobody is on a stale build for long here anyway.
  useHoldBuildRefresh(true);

  return (
    <BackgroundAnalysisProvider>
      <ImpersonationBanner />
      <div className="flex min-h-svh flex-col items-center px-4 sm:px-6 md:px-10">
        <div className="flex w-full min-w-0 items-center justify-between gap-3 py-6">
          <Logo />
          <SignedInIndicator />
        </div>
        <div className="flex w-full max-w-4xl flex-1 items-center justify-center pb-16">
          <div className="w-full">
            <Outlet />
          </div>
        </div>
      </div>
    </BackgroundAnalysisProvider>
  );
}
